// src/components/WatchPanel.jsx
//
// docs/signals-recommendations-watch-spec.md §6 — Watch mode's frontend
// surface. Deliberately the "lighter touch" option the spec calls out: a
// compact bell/badge (unseen count) mounted beside StreamActivityMonitor in
// MainApp.js, opening a dropdown listing proactive trend-anomaly alerts
// (proxy-server/watch/*.js). No MUI (MainApp.js doesn't use it — matches
// StreamActivityMonitor/LiveObserverPanel's plain-inline-style idiom), no
// new dependency.
//
// Prop-driven (not a useTraffic() context consumer itself) so it stays
// independently testable — mirrors LiveObserverPanel's `rows` prop pattern.
// The mount in MainApp.js wires `alerts`/`onDismiss`/`onClear` from
// TrafficContext's watchAlerts/dismissWatchAlert/clearWatchAlerts.
//
// Deviation from spec §6 (noted, not silently dropped): no toast-on-arrival.
// The spec explicitly allows skipping it ("reusing LiveObserverPanel's
// styling idiom if cheap — else skip toasts and say so") — a second stateful
// overlay + auto-dismiss timer set duplicates LiveObserverPanel's own
// pattern for comparatively little value next to the badge count, which
// already surfaces "something new arrived" the moment it changes.

import React, { useState } from 'react';
import { requestLink } from '../utils/deepLinks';
import { setDetectorEnabled } from '../api/watch';

/** Severity -> chip colors, reusing the ribbon's existing --warn- / --error-
 * CSS vars (App.css) rather than inventing a third palette. Exported (pure,
 * no DOM) so it's unit-testable without rendering. */
export function severityStyle(severity) {
  if (severity === 'fail') {
    return { bg: 'var(--error-bg)', text: 'var(--error-text)', border: 'var(--error-border)', label: 'FAIL' };
  }
  return { bg: 'var(--warn-bg)', text: 'var(--warn-text)', border: 'var(--warn-border)', label: 'WARN' };
}

/** Count of alerts whose id isn't in `seenIds` (Set or array). Exported pure
 * helper — the bell badge's unseen count, and independently testable. */
export function countUnseen(alerts, seenIds) {
  const list = Array.isArray(alerts) ? alerts : [];
  const seen = seenIds instanceof Set ? seenIds : new Set(Array.isArray(seenIds) ? seenIds : []);
  return list.filter((a) => a && a.id && !seen.has(a.id)).length;
}

const panelStyle = {
  position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 360,
  maxHeight: 420, overflowY: 'auto', zIndex: 40,
  background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10,
  boxShadow: '0 rgba(0,0,0,0.15)', fontSize: 12,
};

const bellButtonStyle = {
  position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--line)',
  background: 'var(--card)', cursor: 'pointer', fontSize: 15, lineHeight: 1,
};

const badgeStyle = {
  position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16, padding: '0 4px',
  borderRadius: 8, background: 'var(--error-text)', color: '#fff',
  fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
};

function AlertCard({ alert, onDismiss, muted, onMute }) {
  const sev = severityStyle(alert.severity);
  const evidence = Array.isArray(alert.evidence) ? alert.evidence : [];
  return (
    <div style={{
      border: `1px solid ${sev.border}`, background: sev.bg, borderRadius: 8,
      padding: '8px 10px', margin: '6px 8px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ color: sev.text, fontWeight: 700, fontSize: 10, letterSpacing: 0.4 }}>{sev.label}</span>
        <strong style={{ fontSize: 12, color: 'var(--ink)' }}>{alert.kind}</strong>
        {alert.shadow && (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
            background: 'var(--rec-bg)', color: 'var(--rec-text)', border: '1px solid var(--rec-border)',
          }}>
            shadow
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--muted)' }}>
          {alert.ts ? new Date(alert.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
        </span>
      </div>
      {alert.subject && (
        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>subject: {alert.subject}</div>
      )}
      {alert.summary && (
        <div style={{ fontSize: 12, color: 'var(--ink)', marginTop: 4, lineHeight: 1.4 }}>{alert.summary}</div>
      )}
      {alert.nextStep && (
        <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 4, fontStyle: 'italic' }}>
          next step: {alert.nextStep}
        </div>
      )}
      {evidence.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {evidence.map((requestId) => {
            const href = requestLink(requestId);
            if (!href) return null;
            return (
              <a key={requestId} href={href} style={{ fontSize: 10, color: 'var(--primary)' }}>
                {requestId}
              </a>
            );
          })}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button
          onClick={() => onMute(alert.kind)}
          disabled={muted}
          style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 5, cursor: muted ? 'default' : 'pointer',
            border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-2)',
          }}
        >
          {muted ? 'muted' : `mute ${alert.kind}`}
        </button>
        <button
          onClick={() => onDismiss(alert.id)}
          style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, cursor: 'pointer', border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-2)' }}
        >
          dismiss
        </button>
      </div>
    </div>
  );
}

/**
 * WatchPanel — bell/badge + dropdown alert list.
 *   alerts    — array (newest first), TrafficContext's watchAlerts
 *   onDismiss — (id) => void, dismiss a single alert (TrafficContext's dismissWatchAlert)
 *   onClear   — () => void, clear all (TrafficContext's clearWatchAlerts)
 */
export default function WatchPanel({ alerts = [], onDismiss, onClear }) {
  const [open, setOpen] = useState(false);
  const [seenIds, setSeenIds] = useState(() => new Set());
  const [mutedKinds, setMutedKinds] = useState(() => new Set());
  const [muteError, setMuteError] = useState('');

  const unseen = countUnseen(alerts, seenIds);

  function toggleOpen() {
    setOpen((v) => {
      const next = !v;
      if (next) setSeenIds(new Set(alerts.map((a) => a && a.id).filter(Boolean)));
      return next;
    });
  }

  async function handleMute(kind) {
    setMuteError('');
    setMutedKinds((prev) => new Set(prev).add(kind)); // optimistic
    try {
      await setDetectorEnabled(kind, false);
    } catch (e) {
      setMutedKinds((prev) => { const next = new Set(prev); next.delete(kind); return next; });
      setMuteError(`Could not mute ${kind}: ${e && e.message ? e.message : 'request failed'}`);
    }
  }

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-label="Watch mode alerts"
        onClick={toggleOpen}
        style={bellButtonStyle}
        title="Watch mode alerts"
      >
        🔔
        {unseen > 0 && <span style={badgeStyle}>{unseen > 99 ? '99+' : unseen}</span>}
      </button>

      {open && (
        <div style={panelStyle}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 10px', borderBottom: '1px solid var(--line)',
          }}>
            <strong style={{ fontSize: 12, color: 'var(--ink)' }}>Watch alerts</strong>
            {alerts.length > 0 && (
              <button
                onClick={() => onClear && onClear()}
                style={{ fontSize: 10, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                clear all
              </button>
            )}
          </div>

          {muteError && (
            <div style={{ padding: '6px 10px', fontSize: 10, color: 'var(--error-text)' }}>{muteError}</div>
          )}

          {alerts.length === 0 && (
            <div style={{ padding: '16px 10px', fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>
              No alerts yet — watching.
            </div>
          )}

          {alerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onDismiss={onDismiss || (() => {})}
              muted={mutedKinds.has(alert.kind)}
              onMute={handleMute}
            />
          ))}
        </div>
      )}
    </div>
  );
}
